import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';
import { buildLocallyToggledEntity, extractEntityFromTogglePayload } from '../../utils/toggleEntityState';

const ADMIN = '/admin';

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function extractListPayload(payload, preferredKeys = []) {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  const keysToCheck = [...preferredKeys, 'data', 'items', 'results', 'salons'];
  for (const key of keysToCheck) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
    if (isPlainObject(value)) {
      const nested = extractListPayload(value, preferredKeys);
      if (nested.length > 0) return nested;
    }
  }

  return [];
}

function extractEntityPayload(payload, preferredKeys = []) {
  if (!isPlainObject(payload)) return null;

  const keysToCheck = [...preferredKeys, 'data', 'item', 'result', 'salon'];
  for (const key of keysToCheck) {
    if (isPlainObject(payload[key])) return payload[key];
  }

  return payload;
}

function mergeSalonIntoCollection(collection, salon, fallbackId) {
  const targetId = salon?.id ?? fallbackId;
  if (targetId == null) return collection;

  const index = collection.findIndex((item) => item.id === targetId);
  if (index === -1) return collection;

  const next = [...collection];
  next[index] = salon && typeof salon === 'object' ? { ...next[index], ...salon } : next[index];
  return next;
}

export const SALON_APPROVAL_STATUSES = ['approved', 'rejected', 'applied'];

function applyApprovalState(salon, approvedStatus = 'approved') {
  if (!salon || typeof salon !== 'object') return salon;

  const isApproved = approvedStatus === 'approved';

  return {
    ...salon,
    is_approved: isApproved,
    approved: approvedStatus,
    approval_status: approvedStatus,
  };
}

// GET /admin/get-all-salons (filters: city, is_active, include_inactive)
export const fetchSalons = createAsyncThunk(
  'salon/fetchSalons',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-all-salons`, { params: filters });
      return extractListPayload(data, ['salons']);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch salons');
    }
  }
);

// GET /get-all-salons (public, optional city filter)
export const fetchPublicSalons = createAsyncThunk(
  'salon/fetchPublicSalons',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/get-all-salons', { params: filters });
      return extractListPayload(data, ['salons']);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch salons');
    }
  }
);

// GET /admin/pending-approval-salons
export const fetchPendingApprovalSalons = createAsyncThunk(
  'salon/fetchPendingApprovalSalons',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/pending-approval-salons`);
      return extractListPayload(data, ['salons']);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch pending salons');
    }
  }
);

// PATCH /admin/update-salon-approval/:salonId (body: { approved: 'approved' | 'rejected' | 'applied' })
export const updateSalonApproval = createAsyncThunk(
  'salon/updateSalonApproval',
  async ({ salonId, approved = 'approved' }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`${ADMIN}/update-salon-approval/${salonId}`, { approved });
      return {
        salonId,
        approved,
        salon: extractEntityPayload(data, ['salon']),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update salon approval');
    }
  }
);

// GET /admin/get-salon/:id
export const fetchSalonById = createAsyncThunk(
  'salon/fetchSalonById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-salon/${id}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch salon');
    }
  }
);

// POST /admin/create-salon (FormData or JSON, optional image and hours)
export const createSalon = createAsyncThunk(
  'salon/createSalon',
  async (salonData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${ADMIN}/create-salon`, salonData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to create salon');
    }
  }
);

// PUT /admin/update-salon/:id (FormData or JSON)
export const updateSalon = createAsyncThunk(
  'salon/updateSalon',
  async ({ id, salonData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`${ADMIN}/update-salon/${id}`, salonData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update salon');
    }
  }
);

// DELETE /admin/delete-salon/:id
export const deleteSalon = createAsyncThunk(
  'salon/deleteSalon',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${ADMIN}/delete-salon/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to delete salon');
    }
  }
);

// PATCH /admin/toggle-salon-active/:id
export const toggleSalonActive = createAsyncThunk(
  'salon/toggleSalonActive',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`${ADMIN}/toggle-salon-active/${id}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to toggle salon');
    }
  }
);

// PUT /admin/set-salon-hours/:id (body: { hours: [{ day_of_week, open_time, close_time, is_closed, shift_label }] })
export const setSalonHours = createAsyncThunk(
  'salon/setSalonHours',
  async ({ id, hours }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`${ADMIN}/set-salon-hours/${id}`, { hours });
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to set salon hours');
    }
  }
);

// POST /admin/add-salon-image/:id (FormData with image)
export const addSalonImage = createAsyncThunk(
  'salon/addSalonImage',
  async ({ id, imageData }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${ADMIN}/add-salon-image/${id}`, imageData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to add salon image');
    }
  }
);

// DELETE /admin/delete-salon-image/:id/:imageId
export const deleteSalonImage = createAsyncThunk(
  'salon/deleteSalonImage',
  async ({ id, imageId }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`${ADMIN}/delete-salon-image/${id}/${imageId}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to delete salon image');
    }
  }
);

const initialState = {
  salons: [],
  publicSalons: [],
  pendingApprovalSalons: [],
  selectedSalon: null,
  loading: false,
  pendingApprovalLoading: false,
  error: null,
};

const salonSlice = createSlice({
  name: 'salon',
  initialState,
  reducers: {
    selectSalon: (state, action) => {
      state.selectedSalon = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalons.fulfilled, (state, action) => {
        state.loading = false;
        state.salons = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSalons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch salons';
      })
      .addCase(fetchPendingApprovalSalons.pending, (state) => {
        state.pendingApprovalLoading = true;
        state.error = null;
      })
      .addCase(fetchPendingApprovalSalons.fulfilled, (state, action) => {
        state.pendingApprovalLoading = false;
        state.pendingApprovalSalons = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPendingApprovalSalons.rejected, (state, action) => {
        state.pendingApprovalLoading = false;
        state.error = action.payload || 'Failed to fetch pending salons';
      })
      .addCase(fetchPublicSalons.fulfilled, (state, action) => {
        state.publicSalons = action.payload || [];
      })
      .addCase(fetchSalonById.fulfilled, (state, action) => {
        state.selectedSalon = action.payload;
      })
      .addCase(createSalon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSalon.fulfilled, (state, action) => {
        state.loading = false;
        const salon = action.payload && typeof action.payload === 'object' ? action.payload : null;
        if (salon) state.salons.push(salon);
      })
      .addCase(createSalon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create salon';
      })
      .addCase(updateSalon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSalon.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.salons.findIndex((s) => s.id === action.payload?.id);
        if (index !== -1 && action.payload) state.salons[index] = action.payload;
        if (state.selectedSalon?.id === action.payload?.id) {
          state.selectedSalon = action.payload;
        }
      })
      .addCase(updateSalon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update salon';
      })
      .addCase(deleteSalon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSalon.fulfilled, (state, action) => {
        state.loading = false;
        state.salons = state.salons.filter((s) => s.id !== action.payload);
        state.pendingApprovalSalons = state.pendingApprovalSalons.filter((s) => s.id !== action.payload);
        if (state.selectedSalon?.id === action.payload) state.selectedSalon = null;
      })
      .addCase(deleteSalon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete salon';
      })
      .addCase(toggleSalonActive.fulfilled, (state, action) => {
        const updatedSalon = extractEntityFromTogglePayload(action.payload, ['salon']);
        const targetId = updatedSalon?.id ?? action.meta.arg;

        state.salons = mergeSalonIntoCollection(
          state.salons,
          updatedSalon || buildLocallyToggledEntity(state.salons.find((s) => s.id === targetId)),
          targetId
        );
        state.pendingApprovalSalons = mergeSalonIntoCollection(
          state.pendingApprovalSalons,
          updatedSalon || buildLocallyToggledEntity(state.pendingApprovalSalons.find((s) => s.id === targetId)),
          targetId
        );
        if (state.selectedSalon?.id === targetId) {
          state.selectedSalon = updatedSalon
            ? { ...state.selectedSalon, ...updatedSalon }
            : buildLocallyToggledEntity(state.selectedSalon);
        }
      })
      .addCase(updateSalonApproval.fulfilled, (state, action) => {
        const { salonId, approved } = action.payload;
        const updatedSalon = action.payload?.salon
          ? applyApprovalState(action.payload.salon, approved)
          : applyApprovalState({ id: salonId }, approved);
        const targetId = updatedSalon?.id ?? salonId;

        if (approved === 'applied') {
          state.pendingApprovalSalons = mergeSalonIntoCollection(
            state.pendingApprovalSalons,
            updatedSalon,
            targetId
          );
        } else {
          state.pendingApprovalSalons = state.pendingApprovalSalons.filter((s) => s.id !== targetId);
        }

        if (approved === 'approved') {
          state.salons = mergeSalonIntoCollection(state.salons, updatedSalon, targetId);
        }

        if (state.selectedSalon?.id === targetId) {
          state.selectedSalon = {
            ...state.selectedSalon,
            ...updatedSalon,
          };
        }
      })
      .addCase(setSalonHours.fulfilled, (state, action) => {
        if (state.selectedSalon?.id === action.payload?.id) {
          state.selectedSalon = { ...state.selectedSalon, ...action.payload };
        }
        const idx = state.salons.findIndex((s) => s.id === action.payload?.id);
        if (idx !== -1 && action.payload) state.salons[idx] = { ...state.salons[idx], ...action.payload };
      })
      .addCase(addSalonImage.fulfilled, (state, action) => {
        if (state.selectedSalon?.id === action.payload?.id) {
          state.selectedSalon = { ...state.selectedSalon, ...action.payload };
        }
        const idx = state.salons.findIndex((s) => s.id === action.payload?.id);
        if (idx !== -1 && action.payload) state.salons[idx] = { ...state.salons[idx], ...action.payload };
      })
      .addCase(deleteSalonImage.fulfilled, (state, action) => {
        if (state.selectedSalon?.id === action.payload?.id) {
          state.selectedSalon = { ...state.selectedSalon, ...action.payload };
        }
        const idx = state.salons.findIndex((s) => s.id === action.payload?.id);
        if (idx !== -1 && action.payload) state.salons[idx] = { ...state.salons[idx], ...action.payload };
      });
  },
});

export const { selectSalon, clearError } = salonSlice.actions;
export default salonSlice.reducer;

export const selectSalons = (state) => state.salon.salons;
export const selectPublicSalons = (state) => state.salon.publicSalons;
export const selectPendingApprovalSalons = (state) => state.salon.pendingApprovalSalons;
export const selectSelectedSalon = (state) => state.salon.selectedSalon;
export const selectSalonLoading = (state) => state.salon.loading;
export const selectSalonPendingApprovalLoading = (state) => state.salon.pendingApprovalLoading;
export const selectSalonError = (state) => state.salon.error;

export function useSalon() {
  const dispatch = useDispatch();
  const salons = useSelector(selectSalons);
  const pendingApprovalSalons = useSelector(selectPendingApprovalSalons);
  const selectedSalon = useSelector(selectSelectedSalon);
  const loading = useSelector(selectSalonLoading);
  const pendingApprovalLoading = useSelector(selectSalonPendingApprovalLoading);
  const error = useSelector(selectSalonError);

  return {
    salons,
    pendingApprovalSalons,
    selectedSalon,
    loading,
    pendingApprovalLoading,
    error,
    fetchSalons: useCallback((filters) => dispatch(fetchSalons(filters)), [dispatch]),
    fetchPublicSalons: useCallback((filters) => dispatch(fetchPublicSalons(filters)), [dispatch]),
    fetchPendingApprovalSalons: useCallback(() => dispatch(fetchPendingApprovalSalons()), [dispatch]),
    fetchSalonById: useCallback((id) => dispatch(fetchSalonById(id)), [dispatch]),
    createSalon: useCallback((data) => dispatch(createSalon(data)), [dispatch]),
    updateSalon: useCallback(({ id, salonData }) => dispatch(updateSalon({ id, salonData })), [dispatch]),
    updateSalonApproval: useCallback(
      ({ salonId, approved = 'approved' }) => dispatch(updateSalonApproval({ salonId, approved })),
      [dispatch]
    ),
    deleteSalon: useCallback((id) => dispatch(deleteSalon(id)), [dispatch]),
    toggleSalonActive: useCallback((id) => dispatch(toggleSalonActive(id)), [dispatch]),
    setSalonHours: useCallback(({ id, hours }) => dispatch(setSalonHours({ id, hours })), [dispatch]),
    addSalonImage: useCallback(({ id, imageData }) => dispatch(addSalonImage({ id, imageData })), [dispatch]),
    deleteSalonImage: useCallback(({ id, imageId }) => dispatch(deleteSalonImage({ id, imageId })), [dispatch]),
  };
}
